import { useQueryClient } from '@tanstack/react-query';
import {
  addTemplateToPointStyle,
  createPointStyle,
  deletePointStyle,
  getPointStyle,
  removeTemplateFromPointStyle,
  searchPointStyles,
  updatePointStyle,
} from '../../api/point-styles.api';
import { SearchPointStylesApiRequest } from '../../../../types/requests/point-styles/search-point-styles.request';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { CreatePointStyleApiRequest } from '../../../../types/requests/point-styles/create-point-style.request';
import { UpdatePointStyleApiRequest } from '../../../../types/requests/point-styles/update-point-style.request';
import { PointStyleApiModel } from '../../../../types/models/point-style.model';
import { GetPointStyleApiRequest } from '../../../../types/requests/point-styles/get-point-style.request';
import { DeletePointStyleApiRequest } from '../../../../types/requests/point-styles/delete-point-style.request';
import { AddTemplateToPointStyleApiRequest } from '../../../../types/requests/point-styles/add-template-to-point-style.request';
import { RemoveTemplateFromPointStyleApiRequest } from '../../../../types/requests/point-styles/remove-template-from-point-style.request';

const baseQueryKey = 'point-style';
const searchQueryKey = 'search';

export const useUpdatePointStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdatePointStyleApiRequest) => (await updatePointStyle(params)).data,
    onSuccess: (pointStyle) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' || (queryKey[1] as GetPointStyleApiRequest)?.pointStyleId === pointStyle.id),
      }),
  });
  return [request, { ...result, pointStyle: data }] as const;
};

export const useGetPointStyle = <T extends PointStyleApiModel>(params: GetPointStyleApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getPointStyle<T>(params)).data,
    enabled: Boolean(params.pointStyleId && !Number.isNaN(params.pointStyleId)),
  });
  return { ...result, pointStyle: data } as const;
};

export const useCreatePointStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePointStyleApiRequest) => (await createPointStyle(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, pointStyle: data }] as const;
};

export const useSearchPointStyles = (params: SearchPointStylesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchPointStyles({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeletePointStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeletePointStyleApiRequest) => (await deletePointStyle(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useAddTemplateToPointStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddTemplateToPointStyleApiRequest) => (await addTemplateToPointStyle(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] as GetPointStyleApiRequest)?.pointStyleId === params.pointStyleId,
      }),
  });
  return [request, result] as const;
};

export const useRemoveTemplateFromPointStyle = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveTemplateFromPointStyleApiRequest) =>
      (await removeTemplateFromPointStyle(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] as GetPointStyleApiRequest)?.pointStyleId === params.pointStyleId,
      }),
  });
  return [request, result] as const;
};
